import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { JobGlyph } from '@/components/JobGlyph/JobGlyph';
import { AUDIO_MAX_BYTES, MULTIPART_MAX_BYTES, type JobKind, type Sample } from '@/papermill/api';
import { formatDuration } from '@/papermill/transcript';

import styles from './NewJobForm.module.css';

const LOCALES = [
  { value: 'en-IE', label: 'English (Ireland)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-US', label: 'English (US)' },
];

export interface NewJobFormProps {
  /** A document for Papermill, or a recording for Stylus then Papermill. */
  kind?: Exclude<JobKind, 'live'>;
  onSubmitFile: (file: File, options: { locale?: string }) => void;
  onSubmitSample: (key: string) => void;
  /** Upload progress 0–1 while submitting; null when idle. */
  progress?: number | null;
  /** True once uploaded, while the document is checked before the job starts. */
  checking?: boolean;
  error?: string | null;
  /** Why the document was refused before any job started. */
  rejection?: { title: string; message: string } | null;
  /** Why nothing can be submitted right now, e.g. a job is already running. */
  blocked?: string | null;
  samples?: Sample[] | null;
  samplesLoading?: boolean;
  onShowSamples?: () => void;
}

const COPY = {
  document: {
    drop: 'Drop a document here',
    hint: 'PDF, photo or Office file, up to 20 MB · 1 credit per page',
    choose: 'Choose a document',
    accept: 'application/pdf,image/*,.doc,.docx,.xls,.xlsx,.odt,.rtf,.txt',
    start: 'Start extraction',
    limit: 'over the 20 MB limit',
    samplesTitle: 'No document to hand?',
  },
  audio: {
    drop: 'Drop a recording here',
    hint: 'MP3, M4A, WAV or a video, up to 500 MB · 1 credit per minute',
    choose: 'Choose a recording',
    accept: 'audio/*,video/*,.mp3,.m4a,.wav,.flac,.ogg,.opus,.aac,.amr,.webm,.mp4,.mov',
    start: 'Start transcription',
    limit: 'over the 500 MB limit',
    samplesTitle: 'No recording to hand?',
  },
};

function sizeOf(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** How long a local recording runs, read from its metadata; null until known. */
function useDuration(file: File | null, enabled: boolean): number | null {
  const [measured, setMeasured] = useState<{ file: File; ms: number } | null>(null);
  useEffect(() => {
    if (!file || !enabled || typeof Audio === 'undefined') {
      return;
    }
    const url = URL.createObjectURL(file);
    const probe = new Audio();
    probe.preload = 'metadata';
    probe.onloadedmetadata = () => {
      if (Number.isFinite(probe.duration)) {
        setMeasured({ file, ms: probe.duration * 1000 });
      }
    };
    probe.src = url;
    return () => {
      probe.removeAttribute('src');
      URL.revokeObjectURL(url);
    };
  }, [file, enabled]);
  return measured && measured.file === file ? measured.ms : null;
}

/**
 * Pick a document three ways — drop, browse, or the phone's camera — or a
 * recording by drop or browse; or run a sample of either.
 */
export function NewJobForm({
  kind = 'document',
  onSubmitFile,
  onSubmitSample,
  progress = null,
  checking = false,
  error = null,
  rejection = null,
  blocked = null,
  samples = null,
  samplesLoading = false,
  onShowSamples,
}: NewJobFormProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [locale, setLocale] = useState(LOCALES[0].value);
  const audio = kind === 'audio';
  const copy = COPY[kind];
  const submitting = progress !== null || checking;
  const tooLarge = Boolean(file && file.size > (audio ? AUDIO_MAX_BYTES : MULTIPART_MAX_BYTES));
  const durationMs = useDuration(file, audio);
  const credits = durationMs !== null ? Math.max(1, Math.floor(durationMs / 60_000)) : null;

  const preview = useMemo(
    () => (file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null),
    [file],
  );

  useEffect(() => () => (preview ? URL.revokeObjectURL(preview) : undefined), [preview]);

  function pick(files: FileList | null) {
    const chosen = files?.[0];
    if (chosen) {
      setFile(chosen);
    }
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDragging(false);
    pick(event.dataTransfer.files);
  }

  return (
    <div className={styles.form}>
      {blocked ? <Callout title="One job at a time">{blocked}</Callout> : null}
      {error ? <Callout tone="danger">{error}</Callout> : null}
      {rejection ? (
        <Callout tone="danger" title={rejection.title}>
          {rejection.message} No job was started and no credits were used.
        </Callout>
      ) : null}

      {!file ? (
        <div
          className={`${styles.drop} ${dragging ? styles.dragging : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <span className={styles.dropIcon} aria-hidden="true">
            ⇪
          </span>
          <p className={styles.dropTitle}>{copy.drop}</p>
          <p className={styles.dropHint}>{copy.hint}</p>
          <div className={styles.dropActions}>
            <Button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={Boolean(blocked)}
            >
              Choose a file
            </Button>
            {audio ? null : (
              <Button
                type="button"
                variant="ghost"
                icon="◉"
                onClick={() => cameraInput.current?.click()}
                disabled={Boolean(blocked)}
              >
                Take a photo
              </Button>
            )}
          </div>
          <input
            ref={fileInput}
            className={styles.hidden}
            type="file"
            aria-label={copy.choose}
            accept={copy.accept}
            onChange={(event) => pick(event.target.files)}
          />
          {audio ? null : (
            <input
              ref={cameraInput}
              className={styles.hidden}
              type="file"
              aria-label="Take a photo"
              accept="image/*"
              capture="environment"
              onChange={(event) => pick(event.target.files)}
            />
          )}
        </div>
      ) : (
        <div className={styles.selected}>
          <div className={styles.thumb}>
            {preview ? (
              <img src={preview} alt="" />
            ) : (
              <JobGlyph source={audio ? 'audio' : 'document'} />
            )}
          </div>
          <div className={styles.fileInfo}>
            <p className={styles.fileName}>{file.name}</p>
            <p className={styles.fileMeta}>
              {sizeOf(file.size)}
              {durationMs !== null ? ` · ${formatDuration(durationMs)}` : null}
              {credits !== null
                ? ` · about ${credits} ${credits === 1 ? 'credit' : 'credits'}`
                : null}
              {tooLarge ? <span className={styles.warn}> · {copy.limit}</span> : null}
            </p>
            {audio && !submitting ? (
              <label className={styles.locale}>
                <span>Spoken in</span>
                <select value={locale} onChange={(event) => setLocale(event.target.value)}>
                  {LOCALES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {checking ? (
              <div className={styles.checking} role="status">
                <span className={styles.indeterminate} aria-hidden="true" />
                Checking your document…
              </div>
            ) : progress !== null ? (
              <div
                className={styles.progress}
                role="progressbar"
                aria-label="Uploading"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round((progress ?? 0) * 100)}
              >
                <span style={{ width: `${Math.max(4, (progress ?? 0) * 100)}%` }} />
              </div>
            ) : null}
          </div>
          <div className={styles.selectedActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setFile(null)}
              disabled={submitting}
            >
              Change
            </Button>
            <Button
              type="button"
              size="lg"
              loading={submitting}
              disabled={tooLarge || Boolean(blocked)}
              onClick={() => onSubmitFile(file, audio ? { locale } : {})}
            >
              {checking ? 'Checking…' : copy.start}
            </Button>
          </div>
        </div>
      )}

      <section className={styles.samples} aria-labelledby="samples-heading">
        <div className={styles.samplesHeader}>
          <div>
            <h2 id="samples-heading" className={styles.samplesTitle}>
              {copy.samplesTitle}
            </h2>
            <p className={styles.dropHint}>Run one of ours. Samples are free.</p>
          </div>
          {samples === null ? (
            <Button type="button" variant="ghost" onClick={onShowSamples} loading={samplesLoading}>
              Show samples
            </Button>
          ) : null}
        </div>
        {samples && samples.length === 0 ? (
          <p className={styles.dropHint}>No samples are available right now.</p>
        ) : null}
        {samples && samples.length > 0 ? (
          <ul className={styles.sampleGrid}>
            {samples.map((sample) => (
              <li key={sample.key}>
                <button
                  type="button"
                  className={styles.sample}
                  disabled={submitting || Boolean(blocked)}
                  onClick={() => onSubmitSample(sample.key)}
                >
                  <span className={styles.sampleIcon} aria-hidden="true">
                    <JobGlyph source={audio ? 'audio' : 'document'} sample />
                  </span>
                  <span>
                    <span className={styles.sampleTitle}>{sample.title}</span>
                    <span className={styles.sampleMeta}>
                      {audio ? 'Free · ready in a few minutes' : 'Free · runs instantly'}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
